'use client'

import { validateGiftCard } from '@/lib/actions'
import { useCheckout } from '@/lib/hooks'
import { setPaymentMethodType } from '@/lib/redux'
import { isPast, isValid, parseISO } from 'date-fns'
import React, { use } from 'react'
import { toast } from 'sonner'

import { RefContext, type Refs } from '@/components/providers/ref-provider'
import { InputForm } from '@/components/ui'

interface GiftCardReferenceProps
  extends React.ComponentPropsWithoutRef<'form'> {
  onClose: () => void
}

export const GiftCardReference = ({ onClose }: GiftCardReferenceProps) => {
  const [isPending, startTransition] = React.useTransition()
  const { amountInputRef } = use(RefContext) as Refs
  const {
    payment_method,
    setCurrentStep,
    setCustomReference,
    setAmountToCollect,
  } = useCheckout()

  const handleSubmit = (data: FormData) => {
    const barcode = data.get('gift-card-barcode') as string

    startTransition(async () => {
      try {
        const giftCard = await validateGiftCard(barcode)

        const expiryDate = giftCard?.expiry_date
          ? parseISO(giftCard.expiry_date)
          : null

        if (expiryDate && isValid(expiryDate) && isPast(expiryDate)) {
          toast.error('Gift Card has expired')
          return
        }

        if (!giftCard || !giftCard?.id || giftCard?.balance === '0.00') {
          toast.error('Invalid Gift Card Barcode or Gift Card balance is low')
          return
        }

        setAmountToCollect(Number(giftCard?.balance))
        setCustomReference(giftCard?.id?.toString())
        setPaymentMethodType(
          payment_method?.name ?? payment_method?.group ?? 'other',
        )
        setCurrentStep('COLLECT_PAYMENT')
        amountInputRef?.current?.focus()
        onClose?.()
      } catch (error: any) {
        if (typeof error === 'string') {
          toast.error(error)
          return
        }

        toast.error(error?.message)
        return
      }
    })
  }

  return (
    <form className="flex w-full flex-col gap-3 pt-2" action={handleSubmit}>
      <InputForm
        name="gift-card-barcode"
        type="text"
        noSearch
        placeholder="Gift Card Barcode"
        className="pl-5"
        submitLabel="Submit"
        submitPendingLabel="Saving..."
        disabled={isPending}
      />
    </form>
  )
}
